import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDisclosure, Button, Box } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { ApplicationFormContext } from 'contexts/ApplicationFormContext';
import { updateWipStatus as updateAutoWipStatus } from 'reducers/autoPersonalFormReducer';
import { updateWipStatus as updateHomeWipStatus } from 'reducers/homePersonalFormReducer';

import BaraReferralModal from './BaraReferralModal';
import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';
import { apiGetActivePolicyReferral } from 'api';
import { SECTIONS as AUTO_SECTIONS } from 'views/auto_form/constants';
import { SECTIONS as HOME_SECTIONS } from 'views/home_form/constants';
import {
  WIP_STATUS_APPLICATION_CREATION,
  WIP_STATUS_APPEAL_SUBMITTED,
  WIP_STATUS_BARA_SUBMITTED,
} from 'variables/constants';

interface Props {
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
}

// Rendered only when enable-bara FF is ON — form views handle the FF gate.
const BaraReferralRouter = ({ formStateKey }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useAppDispatch();
  const { uuid } = useParams<{ uuid: string }>();

  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };

  const wipStatus = useAppSelector((state) => state[formStateKey].wip_status);
  const remarks = useAppSelector((state) => state[formStateKey].remarks);
  const files = useAppSelector((state) => state[formStateKey].files);
  const appeal = useAppSelector((state) => state[formStateKey].appeal);
  const formData = useAppSelector((state) => state[formStateKey].data) as any;
  const missingMandatoryFields = useAppSelector(
    (state) => state[formStateKey].missingMandatoryFields,
  );
  // Billing can be completed after the referral
  const billingSection =
    formStateKey === 'autoPersonalForm'
      ? AUTO_SECTIONS.METHOD_OF_PAYMENT
      : HOME_SECTIONS.PREMIUM_INFORMATION;
  const isBaraFixesEnabled = useFeatureFlag('enable-bara-referral-fixes');
  const hasMissingMandatoryFields = isBaraFixesEnabled
    ? missingMandatoryFields.some((field) => field.section !== billingSection)
    : missingMandatoryFields.length > 0;

  const blockingConditions = useMemo(() => {
    const reasons: string[] = [];
    if (formData?.broker_questionnaire?.has_special_circumstances) {
      reasons.push('Special circumstances require UW review');
    }
    const dwellings: any[] = formData?.dwellings ?? [];
    dwellings.forEach((d: any) => {
      const ri = d?.rating_information ?? {};
      if (ri.primary_heating_type?.is_professionally_installed === false) {
        reasons.push('Primary heating system not professionally installed');
      }
      if (ri.auxiliary_heating_type?.is_professionally_installed === false) {
        reasons.push('Auxiliary heating system not professionally installed');
      }
      if (ri.water_heater_type?.is_professionally_installed === false) {
        reasons.push('Water heater not professionally installed');
      }
    });
    const vehicles: any[] = formData?.vehicles ?? [];
    vehicles.forEach((v: any, i: number) => {
      if (v?.is_commercial === true || v?.is_farm === true) {
        const uses = [
          v?.is_commercial === true ? 'commercial' : '',
          v?.is_farm === true ? 'farm' : '',
        ]
          .filter(Boolean)
          .join(' and ');
        const details = [
          v?.is_operating_radius_over_40km === true ||
          v?.is_operating_radius_over_40km === false
            ? `Operating radius >40km: ${
                v.is_operating_radius_over_40km ? 'Yes' : 'No'
              }`
            : '',
          v?.commercial_use_description
            ? `Use: ${v.commercial_use_description}`
            : '',
          v?.merchandise_materials_percentage
            ? `Merchandise/materials carried: ${v.merchandise_materials_percentage}%`
            : '',
        ]
          .filter(Boolean)
          .join('; ');
        reasons.push(
          `Vehicle ${i + 1} marked for ${uses} use${
            details ? ` — ${details}` : ''
          }`,
        );
      }
    });

    const report = formData?.report_of_broker_agent ?? {};
    if (report.has_special_circumstances) {
      reasons.push(
        `Special circumstances require UW review${
          report.special_circumstances_particulars
            ? ` — ${report.special_circumstances_particulars}`
            : ''
        }`,
      );
    }
    if (report.is_residual_market_take_all_comers) {
      reasons.push(
        `Residual-market risk placed under the take-all-comers rule${
          report.residual_market_particulars
            ? ` — ${report.residual_market_particulars}`
            : ''
        }`,
      );
    }
    if (report.is_smap_accessed) {
      reasons.push(
        `SMAP accessed to place this risk${
          report.smap_reference_number
            ? ` — MAP reference: ${report.smap_reference_number}`
            : ''
        }`,
      );
    }
    return reasons;
  }, [formData]);

  const mandatoryFileValidations = getMandatoryFileValidations(formStateKey);

  // True if an active BARA referral already exists for this policy (persists across refresh)
  const [hasActiveReferral, setHasActiveReferral] = useState(false);

  useEffect(() => {
    if (!uuid || wipStatus !== WIP_STATUS_APPLICATION_CREATION) return;
    apiGetActivePolicyReferral(uuid)
      .then((data: any) => {
        setHasActiveReferral(!!data?.referral);
        if (isBaraFixesEnabled && !data?.referral) setShowLockWarning(false);
      })
      .catch(() => {});
  }, [uuid, wipStatus, isBaraFixesEnabled]);

  // canRefer: wip is APPLICATION_CREATION, no active BARA referral, no active PU appeal
  const canRefer =
    wipStatus === WIP_STATUS_APPLICATION_CREATION &&
    !hasActiveReferral &&
    !appeal; // active PU appeal also blocks referral

  // Keep component mounted while modal is open so the modal doesn't unmount mid-interaction
  const shouldShowButton = canRefer || isOpen;

  const dispatchUpdate = useCallback(
    (status: number) => {
      const action =
        formStateKey === 'autoPersonalForm'
          ? updateAutoWipStatus
          : updateHomeWipStatus;
      dispatch(action(status));
    },
    [formStateKey, dispatch],
  );

  const handlePUSuccess = useCallback(
    () => dispatchUpdate(WIP_STATUS_APPEAL_SUBMITTED),
    [dispatchUpdate],
  );

  const [showLockWarning, setShowLockWarning] = useState(false);

  const handleMPSuccess = useCallback(() => {
    setHasActiveReferral(true);
    setShowLockWarning(true);
    if (isBaraFixesEnabled) dispatchUpdate(WIP_STATUS_BARA_SUBMITTED);
  }, [dispatchUpdate, isBaraFixesEnabled]);

  if (!shouldShowButton && !showLockWarning) return null;

  return (
    <>
      {showLockWarning && (
        <Box
          mb="20px"
          px={4}
          py={3}
          bg="orange.50"
          border="1px solid"
          borderColor="orange.300"
          borderRadius="md"
          fontSize="sm"
          color="orange.800"
        >
          ⚠️ Your submission will be locked, no changes can be made until a
          decision has been made. If you want to make any changes, you can
          recall the After Hour Referral before it is assigned to a reviewer.
        </Box>
      )}

      {canRefer ? (
        <Button
          colorScheme="brand"
          width="100%"
          onClick={onOpen}
          mb="20px"
          isDisabled={hasMissingMandatoryFields}
        >
          Refer to Underwriting
        </Button>
      ) : null}

      <BaraReferralModal
        isOpen={isOpen}
        onClose={onClose}
        validations={validations}
        remarks={remarks}
        files={files}
        uuid={uuid || ''}
        mandatoryFileValidations={mandatoryFileValidations}
        appeal={appeal}
        onPUSuccess={handlePUSuccess}
        onMPSuccess={handleMPSuccess}
        blockingConditions={blockingConditions}
      />
    </>
  );
};

export default BaraReferralRouter;
